import { useEffect, useState, FunctionComponent, ReactNode } from 'react';
import { Editor as TinyMCEEditor } from '@tinymce/tinymce-react';
import deepEqual from 'deep-equal';
import { useFlags } from 'launchdarkly-react-client-sdk';
import {
  EditorInitConfiguration,
  EditorInstance,
  EditorProps,
  EditorSetupActionIcon,
  EditorTheme,
  ThemeProp
} from './Editor.proptype';
import { ErrorWrapper } from './ErrorWrapper';
import { TransferPricing, Icons } from './theme';
import { EDITOR_IGNORED_WORDS } from '../../constants';
import { getLanguageToolUrl } from '../../utils/configuration';
// Side-effect import: registers the `languagetool` plugin on the global
// `tinymce` object that the self-hosted bundle from public/tinymce/ exposes.
import './plugins/languagetool';

const listenerMap: Record<string, ReactNode> = {
  [EditorTheme.TransferPricing]: TransferPricing
};

const iconsMap: Record<string, EditorSetupActionIcon[]> = {
  [EditorTheme.TransferPricing]: Icons
};

const toolbar = 'formatting sizeandcolor | bullist numlist | aligntext | table | remainder | undo redo';
const toolbarWithLanguageTool =
  'formatting sizeandcolor | bullist numlist | aligntext | table | languagetool | remainder | undo redo';

// Squiggle styles for LanguageTool annotations (red = spelling, blue = grammar).
const languageToolContentStyle = [
  '.lt-error { cursor: pointer; background-size: 4px 2px; background-repeat: repeat-x; background-position: left bottom; }',
  '.lt-error.lt-spelling { background-image: linear-gradient(to right, transparent 0, transparent 25%, #d32f2f 25%, #d32f2f 50%, transparent 50%, transparent 75%, #d32f2f 75%, #d32f2f 100%); }',
  '.lt-error.lt-grammar { background-image: linear-gradient(to right, transparent 0, transparent 25%, #1976d2 25%, #1976d2 50%, transparent 50%, transparent 75%, #1976d2 75%, #1976d2 100%); }'
].join(' ');

const defaults = {
  init: {
    width: '100%',
    height: '300',
    menubar: false,
    branding: false,
    paste_data_images: true, // eslint-disable-line camelcase
    plugins: ['link autolink image table paste lists link image charmap'],
    toolbar: '',
    // eslint-disable-next-line camelcase
    toolbar_groups: {
      formatting: {
        icon: 'format-icon',
        tooltip: 'Formatting',
        items: 'bold italic underline strikethrough superscript subscript'
      },
      aligntext: {
        icon: 'align-left',
        tooltip: 'alignment',
        items: 'alignleft aligncenter alignright alignjustify'
      },
      tables: {
        icon: 'table',
        tooltip: 'Table',
        items: 'table'
      },
      remainder: {
        icon: 'more-drawer',
        tooltip: 'Additional Options',
        items: 'outdent indent image link charmap removeformat'
      }
    }
  }
};

const Editor = ({
  value,
  id,
  disabled,
  error,
  placeholder,
  theme,
  init,
  onEditorChange,
  onBlur,
  onFocus,
  onSetupActions,
  height
}: EditorProps) => {
  const Theme = (listenerMap[theme] ?? TransferPricing) as FunctionComponent<ThemeProp>;
  const icons = iconsMap[theme] ?? [];

  // editor-languagetool-spellcheck gates the LanguageTool restore (JPD-177).
  // OFF is the kill-switch: the plugin is not loaded and the editor behaves
  // exactly as it does today.
  const { editorLanguagetoolSpellcheck } = useFlags();

  const baseToolbar = editorLanguagetoolSpellcheck ? toolbarWithLanguageTool : toolbar;

  // Compute into mergedInit below rather than mutating the shared module-level
  // `defaults` object — `defaults` is a singleton across all Editor instances
  // (and tests), so mutating it bleeds toolbar state between instances.
  const resolvedToolbar = init?.maximize ? baseToolbar + ' maximize' : baseToolbar;

  const [configHasChanged, setRerenderEditor] = useState(false);
  const [internalInit, setInternalInit] = useState<EditorInitConfiguration | undefined>(init);

  const setup = (editor: EditorInstance) => {
    // Adding icons
    for (const item of [...icons, ...(onSetupActions?.icons ?? [])]) {
      editor.ui.registry.addIcon(item.name, item.icon);
    }

    editor.ui.registry.addMenuButton('sizeandcolor', {
      icon: 'change-case',
      fetch: (callback: (items: string) => void) => {
        const items = 'blockformats fontsizes forecolor backcolor';
        callback(items);
      }
    });

    // Adding toggle buttons
    if (onSetupActions?.toggleButtons) {
      for (const item of onSetupActions.toggleButtons) {
        editor.ui.registry.addToggleButton(item.name, {
          icon: item.icon,
          tooltip: item.tooltip,
          onAction: item.onAction
        });
      }
    }
  };

  // Unfortunately, the easiest way to make sure the editor will be updated and respect React lifecycle
  // is by forcing a remount in case of configuration changes.
  // Since this won't happen that often, it shouldn't be a problem in your app
  const forceEditorRerender = () => {
    setRerenderEditor(true);
    requestAnimationFrame(() => {
      setRerenderEditor(false);
    });
  };

  useEffect(() => {
    if (!deepEqual(init, internalInit)) {
      setInternalInit(init);
      forceEditorRerender();
    }
  }, [init, internalInit]);

  const mergedInit: EditorInitConfiguration = {
    ...defaults.init,
    toolbar: resolvedToolbar,
    height,
    ...internalInit,
    id,
    placeholder,
    setup
  };

  if (editorLanguagetoolSpellcheck) {
    /* eslint-disable camelcase */
    Object.assign(mergedInit, {
      // Append rather than replace so callers that pass their own plugin list
      // still get the spellcheck; dedupe so a caller that already lists
      // 'languagetool' doesn't produce a duplicate plugin entry.
      plugins: [...new Set([...(mergedInit.plugins ?? []), 'languagetool'])],
      languagetool_url: getLanguageToolUrl(),
      languagetool_language: 'en-US',
      languagetool_ignore_list: EDITOR_IGNORED_WORDS,
      // Stays false: Chrome/Edge enhanced spellcheck ships editor contents to
      // Google/Microsoft (spell-jacking); LanguageTool keeps the data in-VPC.
      browser_spellcheck: false,
      content_style: [mergedInit.content_style, languageToolContentStyle].filter(Boolean).join(' ')
    });
    /* eslint-enable camelcase */
  }

  return configHasChanged ? null : (
    <ErrorWrapper length={value ? value.length : 0}>
      <Theme error={error}>
        <TinyMCEEditor
          key={id}
          id={id}
          init={mergedInit}
          disabled={disabled}
          value={value}
          onEditorChange={onEditorChange}
          onBlur={onBlur}
          onFocus={onFocus}
        />
      </Theme>
    </ErrorWrapper>
  );
};

export default Editor;
